import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Dialog from '@rio-cloud/rio-uikit/lib/es/Dialog';
import DevicesFiltersForm from '~/features/devices/components/deviceFilters/DevicesFiltersForm';
import DeviceFilterDisplay from '~/features/devices/components/DeviceFilterDisplay';
import DevicesFiltersMenu from '~/features/devices/components/DevicesFiltersMenu';
import ShowResultsButton from '~/features/devices/components/deviceFilters/ShowResultsButton';
import { ALL, DEVICE_STATUS_NAME_PROD_MODE } from '~/features/base/constants/filterOptions';
import { InstalledDeliverableRange } from '~/features/deliverables/utils/InstalledDeliverableRange';
import { parseQuery, stringifyToQuery } from '~/features/base/utils/query';
import compact from 'lodash/fp/compact';
import split from 'lodash/fp/split';

interface FiltersDialogProps {
    show: boolean;
    onHide: () => void;
}

const DevicesFiltersDialog: React.FC<FiltersDialogProps> = ({ show, onHide }) => {
    const history = useHistory();
    const location = useLocation();
    const filtersContainerRef = useRef<HTMLDivElement>(null);
    const defaultValues = useMemo(() => {
        const parsedQuery = parseQuery(location.search);
        const { swVersionMin, swVersionMax, ...restQuery } = parsedQuery;
        const properQuery = {
            ...restQuery,
            installedDeliverables: split(',', parsedQuery.installedDeliverables).map(d => InstalledDeliverableRange.create(d)),
            excludeDistroVersions: compact(split(',', parsedQuery.excludeDistroVersions)),
            vehicleGroupWhitelist: compact(split(',', parsedQuery.vehicleGroupWhitelist)),
            deviceWhitelist: compact(split(',', parsedQuery.deviceWhitelist)),
            vehicleGroupBlacklist: compact(split(',', parsedQuery.vehicleGroupBlacklist)),
            vehicleVariantsWhitelist: compact(split(',', parsedQuery.vehicleVariantsWhitelist)),
            vehicleManufacturerSparePartNumber: compact(split(',', parsedQuery.vehicleManufacturerSparePartNumber)),
            lastEssentialImageReportedVersion: compact(split(',', parsedQuery.lastEssentialImageReportedVersion)),
            distroVersion: compact(split(',', parsedQuery.distroVersion)),
            swVersion: parsedQuery.swVersion ? (parsedQuery.swVersion.includes(',') ? compact(split(',', parsedQuery.swVersion)) : [parsedQuery.swVersion]) : undefined,
            swVersionRange: swVersionMin || swVersionMax ? { minVersion: swVersionMin, maxVersion: swVersionMax } : undefined,
            deviceType: compact(split(',', parsedQuery.deviceType)),
            deviceStatusType: compact(split(',', parsedQuery.deviceStatusType)),
            deviceStatusNames: compact(split(',', parsedQuery.deviceStatusNames)),
            hwVariantList: compact(split(',', parsedQuery.hwVariantList)),
            deviceBlacklist: compact(split(',', parsedQuery.deviceBlacklist)),
            hasMmtProperties: parsedQuery.hasMmtProperties,
            vehicleEnergyTypes: compact(split(',', parsedQuery.vehicleEnergyTypes)),
            mmtHeadUnits: compact(split(',', parsedQuery.mmtHeadUnits)),
            mmtFunctions: compact(split(',', parsedQuery.mmtFunctions)),
            ivdMismatch: parsedQuery.ivdMismatch === 'true' || parsedQuery.ivdMismatch === true ? 'true' :
                        parsedQuery.ivdMismatch === 'false' || parsedQuery.ivdMismatch === false ? 'false' :
                        parsedQuery.ivdMismatch === 'unknown' ? 'unknown' : 'all',
         };
        return properQuery;


    }, [location.search]);

    const methods = useForm();
    const { handleSubmit, control, getValues, reset, watch, setValue } = methods;
    const currentValues = watch();

    const updateFilter = useCallback((filters: any) => {
        Object.entries(filters).forEach(([key, value]) => {
            setValue(key, value);
        });
    }, [setValue]);

    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        if (show && !hasInitialized) {
            Object.entries(defaultValues).forEach(([key, value]) => {
                setValue(key, value);
            });
            setHasInitialized(true);
        }
        if (!show) {
            setHasInitialized(false);
            reset({});
        }
    }, [show, hasInitialized, defaultValues, setValue]);

    const processFilters = (filters: any) => {
        let processedFilters = {};
        Object.entries(filters).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '' || value === ALL || (Array.isArray(value) && value.length === 0)) {
                return;
            }
            if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
                return;
            }

            const handler = fieldHandlers[key];
            if (handler) {
                const result = handler(value, processedFilters);
                if (result && typeof result === 'object' && result._isRange) {
                    Object.assign(processedFilters, result.params);
                } else if (result !== undefined) {
                    processedFilters[key] = result;
                }
            } else {
                processedFilters[key] = String(value);
            }
        });
        return processedFilters;
    };



    const fieldHandlers = {
        installedDeliverables: (value: any[]) => value.map(d => InstalledDeliverableRange.stringify(d)),
        deviceStatusNames: (value: any[], filters: any) => Array.isArray(value) ? value : compact(split(',', value)),
        hwVariantList: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        deviceType: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        deviceStatusType: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        vehicleManufacturerSparePartNumber: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        lastEssentialImageReportedVersion: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        distroVersion: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        swVersion: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        swVersionRange: (value: any) => {
            if (value && typeof value === 'object') {
                const { minVersion, maxVersion } = value;
                return {
                    _isRange: true,
                    params: {
                        ...(minVersion && { swVersionMin: minVersion }),
                        ...(maxVersion && { swVersionMax: maxVersion })
                    }
                };
            }
            return undefined;
        },
        vehicleVariantsWhitelist: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        excludeDistroVersions: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        vehicleGroupWhitelist: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        deviceWhitelist: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        vehicleGroupBlacklist: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        deviceBlacklist: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        productionMode: (value: any) => value === 'true' || value === true ? 'true' : value === 'false' || value === false ? 'false' : undefined,
        ivdMismatch: (value: any) => value === 'all' ? undefined : value,
        hasMmtProperties: (value: any) => value === 'all' ? undefined : value === 'true' || value === true ? 'true' : value === 'false' || value === false ? 'false' : undefined,
        vehicleEnergyTypes: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        mmtHeadUnits: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value)),
        mmtFunctions: (value: any[]) => Array.isArray(value) ? value : compact(split(',', value))
    };

    const onSubmit = () => {
        const filters = getValues();
        const processedFilters = processFilters(filters);

        const queryString = stringifyToQuery(processedFilters);
        history.push({ search: queryString });
        onHide();
    };

    const onClearAll = () => {
        reset({replaced: false});
    };

    const scrollToSection = (sectionId: string) => {
        setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (filtersContainerRef.current && element) {
                const container = filtersContainerRef.current;
                const elementTop = element.offsetTop;
                container.scrollTo({ top: elementTop, behavior: 'smooth' });
            }
        }, 100);
    };



    const body = (
        <div className='flex-column'>
            <div className='sticky-top bg-white border-bottom pb-2 margin-bottom-10' style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                <DeviceFilterDisplay defaultedQuery={currentValues} updateFilter={updateFilter} />
            </div>
            <div className='row gap-20 height-500' style={{ overflow: 'hidden' }}>
                <DevicesFiltersMenu onSectionClick={scrollToSection} />
                <div className='col-9'>
                    <div className='overflow-auto height-500' ref={filtersContainerRef}>
                        <DevicesFiltersForm control={control} setValue={setValue}/>
                    </div>
                </div>
            </div>
        </div>
    );

    const footer = (
        <div className='btn-toolbar justify-content-between'>
            <button className='btn btn-default' onClick={onClearAll}>
                <span className='rioglyph rioglyph-trash margin-right-10' aria-hidden='true'></span>
                <FormattedMessage
                    id='intl-msg:clearAll'
                    defaultMessage='Clear all'
                />
            </button>
            <ShowResultsButton
                filters={currentValues}
                onSubmit={handleSubmit(onSubmit)}
                processFilters={processFilters}
            />
        </div>
    );

    return (
        <Dialog
            show={show}
            onHide={onHide}
            title={<h3 className={'margin-0'}><FormattedMessage id='intl-msg:filters' defaultMessage='intl-msg:filters'/></h3>}
            body={body}
            footer={footer}
        />
    );
};

export default DevicesFiltersDialog;
